import {
  CommonPublicClientOptions,
  GraphQLOptionsV6,
  GraphQLResponseV6,
} from '@aws-amplify/api-graphql';
import { generateClient } from '@aws-amplify/api';
import { CustomHeaders } from '@aws-amplify/data-schema/runtime';
import * as logger from './logger';
const MAX_RETRIES = 5

/**
 *
 * @param options - GraphQLOptions
 * @param returnError - Flag to indicate that the caller will handle the error
 * @param headers - Additional GQL Headers
 * @param backoff - Attempt to retry failed attempts, defaults to `true`
 * @param currentRetry - The current retry (used internally for recursion purposes)
 * @returns Promise<GraphQLResult<any>> | Observable<object>
 *
 * A wrapper around `API.graphql` that allows graceful retries for Query/Mutation operations that slowly backs off.
 * Does not apply for subscriptions (observables) currently
 * Attempts to maintain (mostly) the same function signature as `API.graphql`
 * NOTE: This function throws an error whereas the normal version does not, please handle appropriately
 */
function gracefulQuery<
  FALLBACK_TYPES = unknown,
  TYPED_GQL_STRING extends string = string,
  Options extends CommonPublicClientOptions = object,
>(
  options: GraphQLOptionsV6<FALLBACK_TYPES, TYPED_GQL_STRING, Options>,
  headers?: CustomHeaders,
  backoff: boolean = true,
  currentRetry: number = 1,
  isErrorRetryable?: (error: any) => boolean,
): Promise<GraphQLResponseV6<FALLBACK_TYPES, TYPED_GQL_STRING>> {
  const appsyncClient = generateClient();
  const numbersOfRetries = currentRetry
  const delayForNextIteration = Math.pow(2, numbersOfRetries) * 1000

  // [NOTE] - Inline Async wrapper, which is a bit easier to work with than `Promise.resolve` syntax
  const rv = (async () => {
    try {
      logger.graphqlClient.debug(`Attempting GQL request ${numbersOfRetries}`)
      const response = await appsyncClient.graphql(options, headers);
      return response;
    }
    catch (error: any) {
      logger.graphqlClient.error('Error attempting GQL request', error);

      if (numbersOfRetries >= MAX_RETRIES || (isErrorRetryable && !isErrorRetryable(error))) {
        logger.graphqlClient.error(`Failed GQL call after ${numbersOfRetries} attempt(s)`);
        throw error;
      }

      logger.graphqlClient.debug(`Retrying Graphql request in ${delayForNextIteration}`);
      await new Promise((resolve) => setTimeout(resolve, delayForNextIteration));
      return gracefulQuery<FALLBACK_TYPES, TYPED_GQL_STRING, Options>(
        options,
        headers,
        backoff,
        currentRetry + 1);
    }
  })();

  return rv
}

export default {
  gracefulQuery,
}
